<template lang="pug">
div(
    :class="{ filled: filled, disabled: disabled }"
    class="tl-input-document-upload"
)
    div(
        v-if="title"
        class="tl-title"
    ) {{ title }}

    div(class="tl-input-wrapper")
        div(class="tl-label")
            div(:class="(documentsError || error) && 'error'") {{ label }}

        div(class="documents")
            div(
                v-for="(document, index) in Object.values(modelState)"
                :key="index"
                class="document"
                :class="{ 'disabled': disabled || document.disabled }"
            )
                VeriffFileUpload(
                    v-model="document.files"
                    :sides="2"
                    :extensions="extensions"
                    :loading="document.loading"
                    :progress="document.progress"
                    :disabled="disabled || document.disabled"
                    :min-file-size="minFileSize"
                    :max-file-size="maxFileSize"
                    :label="t('input-document-upload.document.title', {id: index + 1 })"
                    :error="document.error"
                    @error="document.error = $event"
                    @update:model-value="document.error = ''"
                )
                i(
                    class="remove-document mi-close"
                    @click.stop="removeDocument(document)"
                )

            ActionButton(
                :label="`+ ${ documentsLength > 0 ? t('input-document-upload.document.add-another') : t('input-document-upload.document.add') }`"
                secondary
                :disabled="disabled"
                @click="addDocument"
            )

        ErrorText(
            v-if="error?.length"
            class="general-error"
            :error="error"
        )
</template>

<script setup>
import {
    ref, watch, computed, onMounted,
} from 'vue'
import { v4 as uuid } from 'uuid'
import { useI18n } from 'widget-components/utilities/i18n'
import VeriffFileUpload from 'widget-components/components/VeriffFileUpload/VeriffFileUpload.vue'
import ActionButton from 'widget-components/components/ActionButton/ActionButton.vue'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'

const { t } = useI18n()

const props = defineProps({
    modelValue: {
        type: Object,
        default: () => ({}),
    },
    disabled: Boolean,
    minFileSize: {
        type: Number,
        default: 0.04 * 1024 * 1024, // 40KB
    },
    maxFileSize: {
        type: Number,
        default: 10 * 1024 * 1024, // 10MB
    },
    extensions: {
        type: String,
        default: 'jpg,jpeg,png',
    },
    error: {
        type: [String, Boolean],
        default: undefined,
    },
    label: String,
    title: String,
    tooltip: String,
})

const documentsError = computed(
    () => Object
        .values(modelState.value)
        .find(document => document.error?.length)?.error || '',
)

const documentsLength = computed(
    () => Object
        .values(modelState.value)
        .length || 0,
)

onMounted(() => {
    watch(documentsError, () => {
        emit('error', documentsError?.value || '')
    }, { immediate: true })
})

const emit = defineEmits(['update:modelValue', 'error'])

const modelState = ref(props.modelValue)
const modelValue = computed(() => props.modelValue)

const filled = computed(() => !!Object.values(modelState.value)?.length)
const label = computed(() => props.label)

const addDocument = () => {
    const id = uuid()
    modelState.value = {
        ...modelState.value,
        [id]: {
            inputFileId: id,
            files: {},
        },
    }
}

const removeDocument = (document) => {
    if (props.disabled || document.disabled) {
        return
    }
    const newState = modelState.value
    delete newState[document.inputFileId]
    modelState.value = newState
}

onMounted(async () => {
    if (props.defaultValue) {
        modelState.value = props.defaultValue
    }
})

watch(modelState, (val) => {
    emit('update:modelValue', val)
}, { deep: true })

watch(modelValue, (val) => {
    if (val !== modelState.value) modelState.value = val
}, { deep: true })

</script>

<style scoped lang="sass">
@import 'VeriffDocumentUpload'
</style>
